import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import storage from 'utils/storage';
import { AuthenticationType, LoginCredentials, User, RegisterCredentials } from '@novaheal/types';
import { AxiosError } from 'axios';
import * as braze from '@braze/web-sdk';
import { isBrazeInitialized } from 'consent/initBraze';
import { SessionStorageKey } from 'hooks/useSessionStorage';
import { axios } from './axios';
import { parseJwt } from './auth/parseJwt';
import { loginWithEmail, registerWithEmail } from './auth/local/local-auth';
import { loginWithGoogle, registerWithGoogle } from './auth/google/google-auth';
import { logFirebaseUserFlow } from './firebaseLogs';
import { trackMetaCompleteRegistration } from './metaPixelEvents';
import { registerWithApple } from './auth/apple/apple-auth';

const defaultStaleTime = 5 * 60 * 1000;
const defaultGcTime = 15 * 60 * 1000;

const userKey = ['authenticated-user'];

export async function refreshUserToken(): Promise<boolean> {
  try {
    const response = await axios.post('/auth/refresh');
    if (response.status === 200) {
      storage.setToken(response.data.accessToken);
      return true;
    }
    if (isBrazeInitialized()) {
      braze.wipeData();
    }
    return false;
  } catch (error) {
    if (isBrazeInitialized()) {
      braze.wipeData();
    }
    return false;
  }
}

export async function activateAdminImpersonationSession(
  adminToken: string,
  pin: string
): Promise<{ accessToken: string }> {
  const response = await axios.post<{ accessToken: string }>('/auth/admin/activate-session', {
    adminToken,
    pin,
  });
  return response.data;
}

async function userFn(): Promise<User | null> {
  try {
    const token = storage.getToken();
    if (token) {
      const parsedToken = parseJwt(token);
      const isExpired = parsedToken.exp * 1000 < Date.now();
      if (isExpired) {
        const hasToken = await refreshUserToken();
        if (!hasToken) return null;
      }
      const { data: user } = await axios.get<User>('/user/me');

      if (user && user.brazeId && isBrazeInitialized()) {
        braze.changeUser(user.brazeId);
        braze.openSession();
      }
      if (!storage.getTenant() && user?.b2bSchoolId) {
        storage.setTenant(user.b2bSchoolId);
      }
      return user;
    }
    return null;
  } catch (error) {
    return null;
  }
}

const useUser = (
  options?: Omit<UseQueryOptions<User | null, Error, User | null, QueryKey>, 'queryKey' | 'queryFn'>
): UseQueryResult<User | null, Error> =>
  useQuery({
    queryKey: userKey,
    queryFn: userFn,
    // enabled: Boolean(storage.getToken()),
    gcTime: defaultGcTime,
    staleTime: defaultStaleTime,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    ...options,
  });

async function loginFn(credentials: LoginCredentials): Promise<User | null> {
  switch (credentials.authenticationType) {
    case AuthenticationType.EMAIL:
      return loginWithEmail(credentials);
    case AuthenticationType.GOOGLE:
      return loginWithGoogle(credentials);
    case AuthenticationType.APPLE:
      return registerWithApple(credentials);
    default:
      return credentials;
  }
}

const useLogin = (
  options?: Omit<UseMutationOptions<User | null, Error, LoginCredentials>, 'mutationFn'>
): UseMutationResult<User | null, Error, LoginCredentials, unknown> => {
  const queryClient = useQueryClient();

  const setUser = (data: User | null): void => {
    queryClient.setQueryData<User | null>(userKey, data);
    if (data && data.brazeId && isBrazeInitialized()) {
      braze.changeUser(data.brazeId);
      braze.openSession();
    }
    storage.clearTenant();
    if (data?.b2bSchoolId) {
      storage.setTenant(data.b2bSchoolId);
    }
  };

  return useMutation({
    mutationFn: loginFn,
    // Legacy auth errors are status-only (no codeError). Suppress avoids double toasts;
    // TODO(error-handling): migrate to ERROR_CODES — see frontend/docs/error-handling.md.
    meta: { suppressGlobalMutationError: true },
    ...options,
    onSuccess: (user, ...rest) => {
      setUser(user ?? null);
      options?.onSuccess?.(user, ...rest);
    },
  });
};

async function registerFn(credentials: RegisterCredentials): Promise<User | null> {
  switch (credentials.authenticationType) {
    case AuthenticationType.GOOGLE:
      return registerWithGoogle(credentials);
    case AuthenticationType.EMAIL:
      return registerWithEmail(credentials);
    case AuthenticationType.APPLE:
      return registerWithApple(credentials);
    default:
      return credentials;
  }
}

const useRegister = (
  options?: Omit<UseMutationOptions<User | null, Error, RegisterCredentials>, 'mutationFn'>
): UseMutationResult<User | null, Error, RegisterCredentials, unknown> => {
  const queryClient = useQueryClient();

  const setUser = (data: User | null): void => {
    queryClient.setQueryData<User | null>(userKey, data);
    if (data && data.brazeId && isBrazeInitialized()) {
      braze.changeUser(data.brazeId);
      braze.openSession();
    }
  };

  return useMutation({
    mutationFn: registerFn,
    // TODO(error-handling): migrate to ERROR_CODES — see frontend/docs/error-handling.md.
    meta: { suppressGlobalMutationError: true },
    ...options,
    onSuccess: (user, variables, ...rest) => {
      setUser(user ?? null);
      if (user) {
        trackMetaCompleteRegistration({ registerMethod: variables.authenticationType });
      }
      options?.onSuccess?.(user, variables, ...rest);
    },
  });
};

async function logoutFn(): Promise<User | undefined> {
  let user: User | undefined;
  try {
    const { data: userData } = await axios.get('/user/me');
    user = userData;
    const { data } = await axios.post('/auth/logout');
    sessionStorage.setItem('hasLoggedOut', 'true');
    return data;
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 401) {
      await refreshUserToken();
      const { data } = await axios.post('/auth/logout');
      return data;
    }
  } finally {
    storage.clearStorage();
    logFirebaseUserFlow('logout', undefined, user);
  }
}

const useLogout = (
  options?: UseMutationOptions<unknown, Error, unknown>
): UseMutationResult<unknown, Error, unknown, unknown> => {
  const queryClient = useQueryClient();

  const setUser = (data: User | null): void => {
    queryClient.setQueryData<User | null>(userKey, data);
  };
  return useMutation({
    mutationFn: logoutFn,
    ...options,
    onSuccess: (...args) => {
      setUser(null);
      queryClient.removeQueries({ queryKey: ['education-progression-prompt'] });
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(SessionStorageKey.EDUCATION_PROGRESSION_HANDLED_PROMPT_IDS);
      }
      options?.onSuccess?.(...args);
      // TODO: Check wipeData n destroy usage
      if (isBrazeInitialized()) {
        braze.wipeData();
      }
      // braze.destroy(); // Clear Braze data on logout to prevent data leakage between users
    },
  });
};

export async function loadUser(): Promise<User | null> {
  try {
    const adminToken = storage.getAdminToken();
    const token = adminToken || storage.getToken();
    if (token) {
      const parsedToken = parseJwt(token);
      const isExpired = parsedToken.exp * 1000 < Date.now();
      if (isExpired) {
        const hasToken = await refreshUserToken();
        if (!hasToken) return null;
      }
      const { data: user } = await axios.get('/user/me');
      return user;
    }
    return null;
  } catch (error) {
    return null;
  }
}

export { useUser, useLogin, useRegister, useLogout };
